import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSettings } from '@/contexts/SettingsContext';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Search,
  FileText,
  Building,
  Mail,
  Phone,
  MapPin,
  Globe,
  User,
  X,
} from 'lucide-react';
import { StepIndicator } from '@/components/auth/StepIndicator';
import { getLogoUrl } from '@/utils/logoUtils';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

interface FormData {
  // Step 1: Institute Selection
  selectedInstitutes: string[];
  
  // Step 2: Agent Information
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  registeredCompanyName: string;
  tradingName: string;
  companyEmail: string;
  officeAddress: string;
  officeCountry: string;
  officeState: string;
  officeCity: string;
  officePostalCode: string;
  website: string;
  role: string;
  jobRoles: string[];
  recruitmentRegions: string[];
  representsHeadquarters: boolean;
  
  // Step 3: Business Licence
  businessLicenceFile: File | null;
  businessLicenceTranslationFile: File | null;
  icefAccreditationFile: File | null;
  britishCouncilCertificateFile: File | null;
  companyRegistrationNumber: string;
  taxNumber: string;
  yearsOfOperation: string;
  incorporationDate: string;
  expiryDate: string;
  doesNotExpire: boolean;
  registeredAddressSameAsOffice: boolean;
  registeredCompanyAddress: string;
  registeredCompanyCity: string;
  registeredCompanyCountry: string;
  registeredCompanyZip: string;
  registeredCompanyState: string;
  
  // Step 4: Terms and Conditions
  acceptTerms: boolean;
  
  // For registration
  password: string;
  confirmPassword: string;
}

interface StepErrors {
  [key: number]: string;
}

const JOB_ROLES = [
  'Business Owner',
  'Contract Signer',
  'Admissions',
  'Finance',
  'IT',
  'Legal',
  'Marketing',
  'Sales',
  'Executive Leadership',
  'Operations',
  'Administrator',
  'Partnerships Manager',
];

const RECRUITMENT_REGIONS = [
  'Africa',
  'ANZ',
  'Brazil',
  'Caribbean',
  'China',
  'CIS',
  'East Asia',
  'Europe',
  'India',
  'LATAM',
  'Middle East',
  'North Africa',
  'North America',
  'Pakistan',
  'Philippines',
  'SEA',
  'South Asia',
  'Turkey',
  'Vietnam',
  'Bangladesh',
  'Sri Lanka',
];

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Australia', 'Austria', 'Bangladesh',
  'Belgium', 'Brazil', 'Canada', 'China', 'Colombia', 'Denmark', 'Egypt', 'Finland',
  'France', 'Germany', 'Ghana', 'Greece', 'India', 'Indonesia', 'Ireland', 'Italy',
  'Japan', 'Kenya', 'Malaysia', 'Mexico', 'Netherlands', 'New Zealand', 'Nigeria',
  'Norway', 'Pakistan', 'Philippines', 'Poland', 'Portugal', 'Russia', 'Singapore',
  'South Africa', 'South Korea', 'Spain', 'Sweden', 'Switzerland', 'Thailand',
  'Turkey', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Vietnam', 'Zimbabwe',
  // Add more countries as needed
];

export const MultiStepRegisterPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [formData, setFormData] = useState<FormData>({
    selectedInstitutes: [],
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    registeredCompanyName: '',
    tradingName: '',
    companyEmail: '',
    officeAddress: '',
    officeCountry: '',
    officeState: '',
    officeCity: '',
    officePostalCode: '',
    website: '',
    role: '',
    jobRoles: [],
    recruitmentRegions: [],
    representsHeadquarters: true,
    businessLicenceFile: null,
    businessLicenceTranslationFile: null,
    icefAccreditationFile: null,
    britishCouncilCertificateFile: null,
    companyRegistrationNumber: '',
    taxNumber: '',
    yearsOfOperation: '',
    incorporationDate: '',
    expiryDate: '',
    doesNotExpire: false,
    registeredAddressSameAsOffice: true,
    registeredCompanyAddress: '',
    registeredCompanyCity: '',
    registeredCompanyCountry: '',
    registeredCompanyZip: '',
    registeredCompanyState: '',
    acceptTerms: false,
    password: '',
    confirmPassword: '',
  });
  
  const [institutes, setInstitutes] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<StepErrors>({});
  const [logoError, setLogoError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { user } = useAuth();
  const { getFavicon, getAuthPageLogo, getTitle } = useSettings();
  const faviconUrl = getFavicon();
  const authPageLogoUrl = getAuthPageLogo();
  const portalTitle = getTitle();
  const navigate = useNavigate();

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch universities/institutes for Step 1
  useEffect(() => {
    if (currentStep === 1) {
      fetchInstitutes();
    }
  }, [currentStep]);

  const fetchInstitutes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/universities`, {
        params: {
          search: searchTerm,
          country: selectedCountry,
          limit: 100,
          exclusive: 'true', // Filter for exclusive universities at API level
        },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });
      // The API now returns only exclusive universities, but we still need to transform
      // the response since it returns program entries, not direct university entries
      const allEntries = response.data.data || [];
      
      // Group by universityId to get unique universities
      const uniqueUniversities = new Map();
      allEntries.forEach((entry: any) => {
        if (!uniqueUniversities.has(entry.universityId)) {
          uniqueUniversities.set(entry.universityId, {
            id: entry.universityId.toString(),
            name: entry.name,
            country: entry.country,
            city: entry.city,
            flag: entry.flag,
            logo: entry.logo || '',
            ranking: entry.ranking,
            isExclusive: entry.isExclusive || true, // Should be true since we filtered
          });
        }
      });
      
      setInstitutes(Array.from(uniqueUniversities.values()));
    } catch (error) {
      console.error('Error fetching institutes:', error);
      // For now, use mock data if API fails
      setInstitutes([]);
    }
  };

  useEffect(() => {
    if (currentStep === 1) {
      fetchInstitutes();
    }
  }, [searchTerm, selectedCountry]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    
    // Clear error for this step when user starts typing
    if (errors[currentStep]) {
      setErrors((prev) => ({
        ...prev,
        [currentStep]: '',
      }));
    }
  };

  const handleMultiSelect = (name: 'jobRoles' | 'recruitmentRegions', value: string) => {
    setFormData((prev) => {
      const current = prev[name];
      const updated = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return {
        ...prev,
        [name]: updated,
      };
    });
  };

  const handleInstituteToggle = (instituteId: string) => {
    setFormData((prev) => {
      const current = prev.selectedInstitutes;
      const updated = current.includes(instituteId)
        ? current.filter((id) => id !== instituteId)
        : [...current, instituteId];
      return {
        ...prev,
        selectedInstitutes: updated,
      };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'businessLicenceFile' | 'businessLicenceTranslationFile' | 'icefAccreditationFile' | 'britishCouncilCertificateFile') => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: StepErrors = { ...errors };

    switch (step) {
      case 1:
        if (formData.selectedInstitutes.length === 0) {
          newErrors[1] = 'Please select at least one institution to partner with';
          setErrors(newErrors);
          return false;
        }
        break;

      case 2:
        if (!formData.firstName.trim()) {
          newErrors[2] = 'First name is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.lastName.trim()) {
          newErrors[2] = 'Last name is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.email.trim()) {
          newErrors[2] = 'Email ID is required';
          setErrors(newErrors);
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
          newErrors[2] = 'Please enter a valid email address';
          setErrors(newErrors);
          return false;
        }
        if (!formData.mobileNumber.trim()) {
          newErrors[2] = 'WhatsApp number is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.registeredCompanyName.trim()) {
          newErrors[2] = 'Registered company name is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.tradingName.trim()) {
          newErrors[2] = 'Trading name is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.companyEmail.trim()) {
          newErrors[2] = 'Company email ID is required';
          setErrors(newErrors);
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail.trim())) {
          newErrors[2] = 'Please enter a valid company email address';
          setErrors(newErrors);
          return false;
        }
        if (!formData.officeAddress.trim()) {
          newErrors[2] = 'Office address is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.officeCountry) {
          newErrors[2] = 'Office country is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.officeCity.trim()) {
          newErrors[2] = 'Office city is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.officePostalCode.trim()) {
          newErrors[2] = 'Office postal code is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.role.trim()) {
          newErrors[2] = 'Role (Designation) is required';
          setErrors(newErrors);
          return false;
        }
        if (formData.jobRoles.length === 0) {
          newErrors[2] = 'Please select at least one job role/function';
          setErrors(newErrors);
          return false;
        }
        if (formData.recruitmentRegions.length === 0) {
          newErrors[2] = 'Please select at least one recruitment region';
          setErrors(newErrors);
          return false;
        }
        break;

      case 3:
        if (!formData.businessLicenceFile) {
          newErrors[3] = 'Please upload your business licence';
          setErrors(newErrors);
          return false;
        }
        if (!formData.companyRegistrationNumber.trim()) {
          newErrors[3] = 'Company registration number is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.incorporationDate) {
          newErrors[3] = 'Incorporation date is required';
          setErrors(newErrors);
          return false;
        }
        if (!formData.doesNotExpire && !formData.expiryDate) {
          newErrors[3] = 'Expiry date is required or select "Does Not Expire"';
          setErrors(newErrors);
          return false;
        }
        if (!formData.registeredAddressSameAsOffice) {
          if (!formData.registeredCompanyAddress.trim()) {
            newErrors[3] = 'Registered company address is required';
            setErrors(newErrors);
            return false;
          }
          if (!formData.registeredCompanyCity.trim()) {
            newErrors[3] = 'Registered company city is required';
            setErrors(newErrors);
            return false;
          }
          if (!formData.registeredCompanyCountry) {
            newErrors[3] = 'Registered company country is required';
            setErrors(newErrors);
            return false;
          }
          if (!formData.registeredCompanyZip.trim()) {
            newErrors[3] = 'Registered company zip/postal code is required';
            setErrors(newErrors);
            return false;
          }
        }
        break;

      case 4:
        if (!formData.acceptTerms) {
          newErrors[4] = 'You must accept the Terms & Conditions to continue';
          setErrors(newErrors);
          return false;
        }
        // Generate password for registration
        if (!formData.password) {
          // Auto-generate a secure password
          const generatedPassword = `Agent${Date.now()}${Math.random().toString(36).substring(2, 10)}!`;
          setFormData((prev) => ({
            ...prev,
            password: generatedPassword,
            confirmPassword: generatedPassword,
          }));
        }
        break;
    }

    // Clear error for this step if validation passes
    delete newErrors[step];
    setErrors(newErrors);
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      // Prepare FormData for file uploads
      const formDataToSend = new FormData();
      
      // Add all form fields
      formDataToSend.append('email', formData.email);
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      if (formData.mobileNumber) formDataToSend.append('mobileNumber', formData.mobileNumber);
      formDataToSend.append('registeredCompanyName', formData.registeredCompanyName);
      formDataToSend.append('tradingName', formData.tradingName);
      formDataToSend.append('companyEmail', formData.companyEmail);
      formDataToSend.append('officeAddress', formData.officeAddress);
      formDataToSend.append('officeCountry', formData.officeCountry);
      if (formData.officeState) formDataToSend.append('officeState', formData.officeState);
      formDataToSend.append('officeCity', formData.officeCity);
      formDataToSend.append('officePostalCode', formData.officePostalCode);
      formDataToSend.append('website', formData.website);
      formDataToSend.append('role', formData.role);
      formDataToSend.append('jobRoles', JSON.stringify(formData.jobRoles));
      formDataToSend.append('recruitmentRegions', JSON.stringify(formData.recruitmentRegions));
      formDataToSend.append('representsHeadquarters', String(formData.representsHeadquarters));
      if (formData.yearsOfOperation) formDataToSend.append('yearsOfOperation', formData.yearsOfOperation);
      if (formData.taxNumber) formDataToSend.append('taxNumber', formData.taxNumber);
      formDataToSend.append('companyRegistrationNumber', formData.companyRegistrationNumber);
      if (formData.incorporationDate) formDataToSend.append('incorporationDate', formData.incorporationDate);
      if (formData.expiryDate) formDataToSend.append('expiryDate', formData.expiryDate);
      formDataToSend.append('doesNotExpire', String(formData.doesNotExpire));
      formDataToSend.append('registeredAddressSameAsOffice', String(formData.registeredAddressSameAsOffice));
      if (formData.registeredCompanyAddress) formDataToSend.append('registeredCompanyAddress', formData.registeredCompanyAddress);
      if (formData.registeredCompanyCity) formDataToSend.append('registeredCompanyCity', formData.registeredCompanyCity);
      if (formData.registeredCompanyCountry) formDataToSend.append('registeredCompanyCountry', formData.registeredCompanyCountry);
      if (formData.registeredCompanyZip) formDataToSend.append('registeredCompanyZip', formData.registeredCompanyZip);
      if (formData.registeredCompanyState) formDataToSend.append('registeredCompanyState', formData.registeredCompanyState);
      formDataToSend.append('selectedInstitutes', JSON.stringify(formData.selectedInstitutes));
      
      // Add files if they exist
      if (formData.businessLicenceFile) {
        formDataToSend.append('businessLicenceFile', formData.businessLicenceFile);
      }
      if (formData.businessLicenceTranslationFile) {
        formDataToSend.append('businessLicenceTranslationFile', formData.businessLicenceTranslationFile);
      }
      if (formData.icefAccreditationFile) {
        formDataToSend.append('icefAccreditationFile', formData.icefAccreditationFile);
      }
      if (formData.britishCouncilCertificateFile) {
        formDataToSend.append('britishCouncilCertificateFile', formData.britishCouncilCertificateFile);
      }

      const response = await axios.post(`${API_BASE_URL}/auth/register-agent`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });
      
      // Show success message
      setIsSuccess(true);
      setSuccessMessage(response.data.message || 'Your registration application has been submitted successfully. Our team will review it and get back to you soon.');
    } catch (error: any) {
      console.error('Registration failed:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Registration failed. Please try again.';
      setErrors({ [currentStep]: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredInstitutes = institutes.filter((institute) => {
    const matchesSearch = !searchTerm || 
      institute.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      institute.country?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCountry = !selectedCountry || institute.country === selectedCountry;
    // All universities should be exclusive since we filter at API level, but double-check
    return matchesSearch && matchesCountry;
  });

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="fs-4 fw-bold text-dark mb-2">Select Institute</h3>
            <p className="text-muted mb-3">Please select the institutions that you would like to partner with:</p>

            <div className="alert alert-info mb-3">
              <div className="d-flex align-items-start">
                <FileText size={20} className="me-2 mt-1" />
                <div>
                  <strong>Instructions</strong>
                  <p className="mb-0 small">
                    This is your first step in joining our global network. Please complete this quick 4-step process 
                    and we'll evaluate your application. Select which institutions you are applying to partner with. 
                    You can revisit this form as many times as you'd like.
                  </p>
                  <p className="mb-0 small mt-2">
                    <strong>Please note:</strong> The onboarding form needs to be completed by the decision-maker 
                    of your company, as it requires confirmation of critical information.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div className="row g-2 mb-2">
                <div className="col-md-4">
                  <select
                    className="form-select"
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                  >
                    <option value="">All Regions</option>
                    <option value="Africa">Africa</option>
                    <option value="Asia">Asia</option>
                    <option value="Europe">Europe</option>
                    <option value="North America">North America</option>
                    <option value="South America">South America</option>
                    <option value="Oceania">Oceania</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <select
                    className="form-select"
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                  >
                    <option value="">All Countries</option>
                    {COUNTRIES.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text">
                      <Search size={18} />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search for an Institute"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">
                  {formData.selectedInstitutes.length} / {filteredInstitutes.length} Institutions Selected
                </span>
              </div>

              <div className="row g-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredInstitutes.length === 0 ? (
                  <div className="col-12 text-center py-3">
                    <p className="text-muted">No institutions found. Try adjusting your search or filters.</p>
                  </div>
                ) : (
                  filteredInstitutes.map((institute) => (
                    <div key={institute.id} className="col-md-4 col-lg-3">
                      <div
                        className={`card h-100 cursor-pointer ${
                          formData.selectedInstitutes.includes(String(institute.id)) ? 'border-primary' : ''
                        }`}
                        onClick={() => handleInstituteToggle(String(institute.id))}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-body text-center">
                          {getLogoUrl(institute.logo) ? (
                            <img
                              src={getLogoUrl(institute.logo) || ''}
                              alt={institute.name}
                              className="img-fluid mb-2"
                              style={{ maxHeight: '60px', maxWidth: '100%', objectFit: 'contain' }}
                              onError={(e) => {
                                // Hide broken image
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center mb-2" style={{ height: '60px' }}>
                              <Building size={32} className="text-muted" />
                            </div>
                          )}
                          <h6 className="card-title small mb-2">{institute.name}</h6>
                          <div className="form-check d-flex justify-content-center">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={formData.selectedInstitutes.includes(String(institute.id))}
                              onChange={() => handleInstituteToggle(String(institute.id))}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h3 className="fs-4 fw-bold text-dark mb-0">Agent Information</h3>
              <span className="text-muted small">2/4</span>
            </div>
            <p className="text-muted mb-3">Please enter your information:</p>

              <div className="row mb-2">
              <div className="col-md-6 mb-2">
                <label className="form-label fw-semibold text-dark">
                  First Name <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <User size={18} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter your first name"
                    required
                  />
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold text-dark">
                  Last Name <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <User size={18} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter your last name"
                    required
                  />
                </div>
              </div>
            </div>

              <div className="row mb-2">
              <div className="col-md-6 mb-2">
                <label className="form-label fw-semibold text-dark">
                  Email ID <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <Mail size={18} className="text-muted" />
                  </span>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold text-dark">
                  WhatsApp Number <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <Phone size={18} className="text-muted" />
                  </span>
                  <input
                    type="tel"
                    className="form-control"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    placeholder="e.g. 08068122576 or +2348068122576"
                    required
                  />
                </div>
                <small className="form-text text-muted">Use the WhatsApp number you will message Scholarvest from. Numbers without a country code default to +234.</small>
              </div>
            </div>

            <hr className="my-4" />

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                Registered Company Name (As on business licence) <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <Building size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  name="registeredCompanyName"
                  value={formData.registeredCompanyName}
                  onChange={handleChange}
                  placeholder="Enter registered company name"
                  required
                />
              </div>
            </div>

              <div className="row mb-2">
              <div className="col-md-6 mb-2">
                <label className="form-label fw-semibold text-dark">
                  Trading Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="tradingName"
                  value={formData.tradingName}
                  onChange={handleChange}
                  placeholder="Enter trading name"
                  required
                />
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-semibold text-dark">
                  Tax Number
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="taxNumber"
                  value={formData.taxNumber}
                  onChange={handleChange}
                  placeholder="Enter tax number"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                Company Email ID <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <Mail size={18} className="text-muted" />
                </span>
                <input
                  type="email"
                  className="form-control"
                  name="companyEmail"
                  value={formData.companyEmail}
                  onChange={handleChange}
                  placeholder="Enter company email"
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                Office Address (Physical Location) <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <MapPin size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  name="officeAddress"
                  value={formData.officeAddress}
                  onChange={handleChange}
                  placeholder="Enter office address"
                  required
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold text-dark">
                  Office Country <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="officeCountry"
                  value={formData.officeCountry}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Country</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold text-dark">Office State</label>
                <input
                  type="text"
                  className="form-control"
                  name="officeState"
                  value={formData.officeState}
                  onChange={handleChange}
                  placeholder="Enter state"
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold text-dark">
                  Office City <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="officeCity"
                  value={formData.officeCity}
                  onChange={handleChange}
                  placeholder="Enter city"
                  required
                />
              </div>
            </div>

              <div className="row mb-2">
              <div className="col-md-6 mb-2">
                <label className="form-label fw-semibold text-dark">
                  Office Postal Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="officePostalCode"
                  value={formData.officePostalCode}
                  onChange={handleChange}
                  placeholder="Enter postal code"
                  required
                />
              </div>
            </div>

              <div className="row mb-2">
              <div className="col-md-6 mb-2">
                <label className="form-label fw-semibold text-dark">
                  Website
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <Globe size={18} className="text-muted" />
                  </span>
                  <input
                    type="url"
                    className="form-control"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold text-dark">
                  Role (Designation) <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  placeholder="Enter your role"
                  required
                />
              </div>
            </div>

            <hr className="my-3" />

            <div className="row mb-2">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold text-dark">
                  Years of Operation
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="yearsOfOperation"
                  value={formData.yearsOfOperation}
                  onChange={handleChange}
                  placeholder="Enter years of operation"
                  min="0"
                />
                <small className="text-muted">(as an education agent)</small>
              </div>
            </div>

            <hr className="my-3" />

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark mb-2">
                Select Job Role/Function <span className="text-danger">*</span>
              </label>
              <div className="row g-2">
                {JOB_ROLES.map((role) => (
                  <div key={role} className="col-md-3 col-sm-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`jobRole-${role}`}
                        checked={formData.jobRoles.includes(role)}
                        onChange={() => handleMultiSelect('jobRoles', role)}
                      />
                      <label className="form-check-label" htmlFor={`jobRole-${role}`}>
                        {role}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <hr className="my-3" />

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark mb-2">
                Select Recruitment Region <span className="text-danger">*</span>
              </label>
              <div className="row g-2">
                {RECRUITMENT_REGIONS.map((region) => (
                  <div key={region} className="col-md-3 col-sm-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`region-${region}`}
                        checked={formData.recruitmentRegions.includes(region)}
                        onChange={() => handleMultiSelect('recruitmentRegions', region)}
                      />
                      <label className="form-check-label" htmlFor={`region-${region}`}>
                        {region}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <hr className="my-4" />

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark mb-3">
                Do you represent your office headquarters?
              </label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="representsHeadquarters"
                    id="headquartersYes"
                    checked={formData.representsHeadquarters === true}
                    onChange={() => setFormData((prev) => ({ ...prev, representsHeadquarters: true }))}
                  />
                  <label className="form-check-label" htmlFor="headquartersYes">
                    Yes
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="representsHeadquarters"
                    id="headquartersNo"
                    checked={formData.representsHeadquarters === false}
                    onChange={() => setFormData((prev) => ({ ...prev, representsHeadquarters: false }))}
                  />
                  <label className="form-check-label" htmlFor="headquartersNo">
                    No
                  </label>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="fs-4 fw-bold text-dark mb-0">Submit Business Licence</h3>
              <span className="text-muted small">3/4</span>
            </div>
            <p className="text-muted mb-3">Please upload your business licence:</p>

            <div className="alert alert-info mb-3">
              <div className="d-flex align-items-start">
                <FileText size={20} className="me-2 mt-1" />
                <div>
                  <strong>Instructions</strong>
                  <ul className="mb-0 small">
                    <li>Please upload a copy of your business licence / Incorporation document.</li>
                    <li>If your original licence is not in English, please upload two files: the original business licence, and a file with the licence translated into English.</li>
                    <li>You can upload both the original and the translation below. Editable file formats will not be accepted.</li>
                    <li>Business licence should match the information provided below.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                Business Licence <span className="text-danger">*</span>
              </label>
              <div className="border rounded p-3">
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, 'businessLicenceFile')}
                />
                {formData.businessLicenceFile && (
                  <div className="mt-2 d-flex align-items-center">
                    <FileText size={16} className="me-2" />
                    <span className="small">{formData.businessLicenceFile.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger ms-auto"
                      onClick={() => setFormData((prev) => ({ ...prev, businessLicenceFile: null }))}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                Business Licence Translation (if applicable)
              </label>
              <div className="border rounded p-3">
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, 'businessLicenceTranslationFile')}
                />
                {formData.businessLicenceTranslationFile && (
                  <div className="mt-2 d-flex align-items-center">
                    <FileText size={16} className="me-2" />
                    <span className="small">{formData.businessLicenceTranslationFile.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger ms-auto"
                      onClick={() => setFormData((prev) => ({ ...prev, businessLicenceTranslationFile: null }))}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                ICEF Accreditation (if available)
              </label>
              <div className="border rounded p-3">
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, 'icefAccreditationFile')}
                />
                {formData.icefAccreditationFile && (
                  <div className="mt-2 d-flex align-items-center">
                    <FileText size={16} className="me-2" />
                    <span className="small">{formData.icefAccreditationFile.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger ms-auto"
                      onClick={() => setFormData((prev) => ({ ...prev, icefAccreditationFile: null }))}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                British Council UK Agent Training Certification (if available)
              </label>
              <div className="border rounded p-3">
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, 'britishCouncilCertificateFile')}
                />
                {formData.britishCouncilCertificateFile && (
                  <div className="mt-2 d-flex align-items-center">
                    <FileText size={16} className="me-2" />
                    <span className="small">{formData.britishCouncilCertificateFile.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger ms-auto"
                      onClick={() => setFormData((prev) => ({ ...prev, britishCouncilCertificateFile: null }))}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <hr className="my-4" />

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                Company Registration Number <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="companyRegistrationNumber"
                value={formData.companyRegistrationNumber}
                onChange={handleChange}
                placeholder="Enter company registration number"
                required
              />
            </div>

              <div className="row mb-2">
              <div className="col-md-6 mb-2">
                <label className="form-label fw-semibold text-dark">
                  Incorporation Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="incorporationDate"
                  value={formData.incorporationDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold text-dark">
                  Expiry Date {!formData.doesNotExpire && <span className="text-danger">*</span>}
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  disabled={formData.doesNotExpire}
                  required={!formData.doesNotExpire}
                />
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="doesNotExpire"
                    checked={formData.doesNotExpire}
                    onChange={handleChange}
                  />
                  <label className="form-check-label small">Does Not Expire</label>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark mb-3">
                Is the registered address the same as the office physical address?
              </label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="registeredAddressSameAsOffice"
                    id="sameAddressYes"
                    checked={formData.registeredAddressSameAsOffice === true}
                    onChange={() => setFormData((prev) => ({ ...prev, registeredAddressSameAsOffice: true }))}
                  />
                  <label className="form-check-label" htmlFor="sameAddressYes">
                    Yes
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="registeredAddressSameAsOffice"
                    id="sameAddressNo"
                    checked={formData.registeredAddressSameAsOffice === false}
                    onChange={() => setFormData((prev) => ({ ...prev, registeredAddressSameAsOffice: false }))}
                  />
                  <label className="form-check-label" htmlFor="sameAddressNo">
                    No
                  </label>
                </div>
              </div>
            </div>

            {!formData.registeredAddressSameAsOffice && (
              <div className="border rounded p-3 bg-light">
                <h6 className="fw-semibold mb-3">Registered Company Address</h6>
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark">
                    Registered Company Address <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="registeredCompanyAddress"
                    value={formData.registeredCompanyAddress}
                    onChange={handleChange}
                    placeholder="Enter registered company address"
                    required
                  />
                </div>
              <div className="row mb-2">
              <div className="col-md-6 mb-2">
                    <label className="form-label fw-semibold text-dark">
                      Registered Company City <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="registeredCompanyCity"
                      value={formData.registeredCompanyCity}
                      onChange={handleChange}
                      placeholder="Enter city"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      Registered Company Country <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="registeredCompanyCountry"
                      value={formData.registeredCompanyCountry}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Country</option>
                      {COUNTRIES.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              <div className="row mb-2">
              <div className="col-md-6 mb-2">
                    <label className="form-label fw-semibold text-dark">
                      Registered Company Zip/Postal Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="registeredCompanyZip"
                      value={formData.registeredCompanyZip}
                      onChange={handleChange}
                      placeholder="Enter zip/postal code"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold text-dark">
                      Registered Company State <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="registeredCompanyState"
                      value={formData.registeredCompanyState}
                      onChange={handleChange}
                      placeholder="Enter state"
                      required
                    />
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            key="step4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h3 className="fs-4 fw-bold text-dark mb-0">Terms and Conditions</h3>
              <span className="text-muted small">4/4</span>
            </div>
            <p className="text-muted mb-3">WELCOME! When accessing Scholarvest Gateway, you agree:</p>

            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body p-3">
                <div className="policy-text" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  <p>
                    Scholarvest is the Data Controller and you, the Representative, are the Data Processor for the purposes
                    of the processing of Protected Data. The Representative shall, and shall ensure its Sub-Processors 
                    and each of its/their personnel shall, at all times comply with all Data Protection Laws in connection 
                    with the processing of Protected Data and shall not by any act or omission cause Scholarvest to be in breach 
                    of any of the Data Protection Laws. By entering Scholarvest Gateway you agree to abide by our{' '}
                    <a href="/privacy-policy" target="_blank" className="text-primary">
                      Privacy Policy
                    </a>{' '}
                    &{' '}
                    <a href="/code-of-conduct" target="_blank" className="text-primary">
                      Scholarvest Quality Shield Code of Conduct
                    </a>.
                  </p>
                  <p>
                    I confirm that any personal data I enter onto Scholarvest Gateway has been sourced lawfully and in accordance
                    with (UK)GDPR and applicable local data protection laws. I confirm that at all times I am acting within 
                    my representative agreements in place with the institutions and abiding by all policies, procedures and 
                    declarations therein to obtain, input and share applicant data lawfully, with the applicant's knowledge 
                    and consent. I understand I am responsible for obtaining the applicant's consent to create their application 
                    via Scholarvest Gateway.
                  </p>
                  <p>
                    You have a unique username and password in order to access your Scholarvest Gateway account. To protect against
                    unauthorised access to account information, you agree not to share accounts and to keep their login details 
                    confidential. Users are wholly responsible for maintaining the confidentiality of their password. If you believe 
                    the security of your username or password has been compromised, reset your password and contact{' '}
                    <a href="mailto:support@scholarvest.com" className="text-primary">
                      support@scholarvest.com
                    </a>.
                  </p>
                  <ul>
                    <li>Recruitment Partners are expected to submit student applications with student details only.</li>
                    <li>Recruitment Partners should not use their details for submitting student applications.</li>
                    <li>
                      Any application received from a recruitment partner with details that do not belong to the student 
                      (example: email address) will violate the institutional representative agreement and Scholarvest Quality Shield
                      Code of Conduct policy and lead to termination of the institutional representative agreement.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                name="acceptTerms"
                id="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                required
              />
              <label className="form-check-label" htmlFor="acceptTerms">
                I accept the Terms & Conditions and Scholarvest Quality Shield Code Of Conduct <span className="text-danger">*</span>
              </label>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-vh-100 position-relative overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--primary, #1e40af) 0%, var(--primary-dark, #1e3a8a) 100%)' }}>
      {/* Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100">
        <div className="position-absolute" style={{ top: '10%', left: '5%', width: '200px', height: '200px', background: 'linear-gradient(135deg, var(--primary, #1e40af) 0%, var(--primary-dark, #1e3a8a) 100%)', borderRadius: '20px', transform: 'rotate(15deg)', opacity: 0.1 }}></div>
        <div className="position-absolute" style={{ top: '20%', right: '10%', width: '150px', height: '150px', background: 'linear-gradient(135deg, var(--primary-dark, #1e3a8a) 0%, var(--primary, #1e40af) 100%)', borderRadius: '50%', opacity: 0.1 }}></div>
        <div className="position-absolute" style={{ bottom: '20%', left: '15%', width: '100px', height: '100px', background: 'linear-gradient(135deg, var(--primary, #1e40af) 0%, var(--primary-dark, #1e3a8a) 100%)', borderRadius: '15px', transform: 'rotate(-30deg)', opacity: 0.1 }}></div>
        <div className="position-absolute" style={{ bottom: '30%', right: '20%', width: '120px', height: '120px', background: 'linear-gradient(135deg, var(--primary, #1e40af) 0%, var(--primary-dark, #1e3a8a) 100%)', borderRadius: '25px', transform: 'rotate(45deg)', opacity: 0.1 }}></div>
      </div>

      <div className="container position-relative" style={{ zIndex: 10 }}>
        <div className="row justify-content-center min-vh-100 align-items-center py-3">
          <div className="col-lg-11 col-xl-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="position-relative"
            >
              {/* Main Content Container */}
              <div className="row g-0 shadow-lg rounded-4 overflow-hidden bg-white" style={{ minHeight: '600px' }}>
                {/* Left Side - Branding */}
                <div className="col-lg-5 position-relative d-none d-lg-flex" style={{ background: 'linear-gradient(135deg, var(--primary, #1e40af) 0%, var(--primary-dark, #1e3a8a) 100%)' }}>
                  <div className="position-relative p-4 h-100 d-flex flex-column justify-content-center text-white">
                    <div className="text-center mb-4">
                      {/* Logo */}
                      <div className="d-flex align-items-center justify-content-center mx-auto mb-3" style={{ maxWidth: '100%', padding: '0 1rem' }}>
                        {logoError ? (
                          <Building size={40} className="text-white" style={{ width: 'clamp(30px, 6vw, 50px)', height: 'clamp(30px, 6vw, 50px)' }} />
                        ) : (
                          <img 
                            src={authPageLogoUrl || faviconUrl || '/scholar_vest_logo.png'} 
                            alt={`${portalTitle} Agent Portal Logo`} 
                            style={{ 
                              height: "auto",
                              width: "auto",
                              maxHeight: "clamp(40px, 10vw, 70px)",
                              maxWidth: "100%",
                              objectFit: "contain",
                              objectPosition: "center"
                            }}
                            onError={() => setLogoError(true)}
                          />
                        )}
                      </div>
                      {/* <h1 className="display-5 fw-bold mb-1">{portalTitle}</h1> */}
                      <p className="fs-6 text-white text-opacity-90">Agent Portal</p>
                    </div>
                    
                    <div className="text-center">
                      <h2 className="fs-3 fw-bold mb-2">Join Our Network!</h2>
                      <p className="fs-5 text-white text-opacity-90 mb-0">
                        Register as an agent and start submitting applications for your clients
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Side - Form */}
                <div className="col-lg-7 p-3 p-md-4 d-flex flex-column">
                  <div className="w-100 flex-grow-1 d-flex flex-column">
                    {/* Step Indicator */}
                    <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />

                    {/* Success Display */}
                    {isSuccess ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex-grow-1 d-flex flex-column justify-content-center align-items-center text-center p-3"
                        style={{ minHeight: '350px' }}
                      >
                        <div className="mb-3">
                          <CheckCircle size={56} className="text-success" />
                        </div>
                        <h3 className="fs-4 fw-bold text-dark mb-2">Application Submitted Successfully!</h3>
                        <p className="text-muted mb-3" style={{ maxWidth: '500px' }}>
                          {successMessage}
                        </p>
                        <div className="alert alert-info" style={{ maxWidth: '500px' }}>
                          <p className="mb-0 small">
                            <strong>What happens next?</strong>
                          </p>
                          <p className="mb-0 small mt-2">
                            Our team will review your application and get back to you via email. 
                            If approved, you'll receive login credentials to access the Agent Portal.
                          </p>
                        </div>
                        <Link
                          to="/login"
                          className="btn btn-primary mt-3"
                        >
                          Go to Login Page
                        </Link>
                      </motion.div>
                    ) : (
                      <>
                        {/* Error Display */}
                        {errors[currentStep] && (
                          <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
                            <AlertCircle size={18} className="me-2" />
                            <small>{errors[currentStep]}</small>
                          </div>
                        )}

                        {/* Step Content */}
                        <div className="flex-grow-1 d-flex flex-column justify-content-center" style={{ minHeight: '350px' }}>
                          <AnimatePresence mode="wait">
                            {renderStepContent()}
                          </AnimatePresence>
                        </div>
                      </>
                    )}

                    {/* Navigation Buttons */}
                    {!isSuccess && (
                      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                        {currentStep > 1 ? (
                          <button
                            type="button"
                            className="btn btn-outline-secondary d-flex align-items-center gap-2"
                            onClick={handleBack}
                            disabled={isLoading}
                          >
                            <ArrowLeft size={18} />
                            Back
                          </button>
                        ) : (
                          <div></div>
                        )}

                        {currentStep < totalSteps ? (
                          <button
                            type="button"
                            className="btn btn-primary d-flex align-items-center gap-2 ms-auto"
                            onClick={handleNext}
                            disabled={isLoading}
                          >
                            Next
                            <ArrowRight size={18} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary btn-lg d-flex align-items-center gap-2 ms-auto"
                            onClick={handleSubmit}
                            disabled={isLoading || !formData.acceptTerms}
                          >
                            {isLoading ? (
                              <>
                                <div className="spinner-border spinner-border-sm" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                Submitting...
                              </>
                            ) : (
                              <>
                                Submit Application
                                <CheckCircle size={18} />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Sign In Link */}
                    <div className="text-center mt-3">
                      <p className="text-muted mb-0 small">
                        Already have an agent account?{' '}
                        <Link to="/login" className="text-primary text-decoration-none fw-semibold">
                          Sign in here
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
